import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { StylesheetType } from '@common/flare-data/enums/stylesheet-type.enum';
import { firstOrNull, makeArray } from '@common/util/array';
import { repeatWhileNull } from '@common/util/repeat-while-null.operator';
import { TopicProperties } from '@portal-core/file-properties/models/topic-properties.model';
import { TopicPropertiesService } from '@portal-core/file-properties/services/topic-properties.service';
import { FormsService } from '@portal-core/forms/services/forms.service';
import { ProjectFilePickerInputComponent } from '@portal-core/project-files/components/project-file-picker-input/project-file-picker-input.component';
import { StylesheetsFileFilter, TemplatePageFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { MetaDataService } from '@portal-core/project-files/services/meta-data.service';
import { InsertVariableDialogData } from '@portal-core/project-files/variables/components/insert-variable-dialog/insert-variable-dialog.component';
import { ProjectVariableSet } from '@portal-core/project-files/variables/models/project-variable-set.model';
import { VariablesInsertService } from '@portal-core/project-files/variables/services/variables-insert.service';
import { VariablesService } from '@portal-core/project-files/variables/services/variables.service';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { isEqual } from 'lodash';
import { combineLatest, filter, merge, Observable, shareReplay, skip, Subject, switchMap, takeUntil } from 'rxjs';

/** The form interface for editing topic properties. */
interface TopicPropertiesForm {
  title: FormControl<string>;
  stylesheets: FormControl<string[]>;
  templatePage: FormControl<string[]>;
  searchable: FormControl<boolean>;
}

/** The component for editing topic properties. */
@Component({
  selector: 'mc-topic-properties',
  templateUrl: './topic-properties.component.html',
  styleUrls: ['./topic-properties.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopicPropertiesComponent implements OnInit, OnDestroy {
  @Input() branchName: string;
  @Input() commitId: string;
  @Input() projectId: number;
  @Input() readonly: boolean = false;
  @Input() topicProperties: TopicProperties;
  @Input() usingWorkspaceTree: boolean = false;
  @Input() showTitle?: boolean;
  @Input() showTemplatePage?: boolean;
  @Input() showSearchable?: boolean;

  @InputObservable('readonly') readonly$: Observable<boolean>;
  @InputObservable('commitId') commitId$: Observable<string>;
  @InputObservable('projectId') projectId$: Observable<number>;
  @InputObservable('topicProperties') topicProperties$: Observable<TopicProperties>;

  @ViewChild('stylesheetInputs', { static: false }) stylesheetInputs: ProjectFilePickerInputComponent;
  @ViewChild('templatePageInput', { static: false }) templatePageInput: ProjectFilePickerInputComponent;

  protected form: FormGroup<TopicPropertiesForm>;

  private props: TopicProperties;
  private ignoreChanges: boolean = false;
  private unsubscribe = new Subject<void>();
  private variableSets$: Observable<ProjectVariableSet[]>;

  // Expose file filters to the template
  StylesheetsFileFilter: string = StylesheetsFileFilter;
  TemplatePageFileFilter: string = TemplatePageFileFilter;

  constructor(
    private formBuilder: FormBuilder,
    private formsService: FormsService,
    private metaDataService: MetaDataService,
    private variablesService: VariablesService,
    private variablesInsertService: VariablesInsertService,
    private topicPropertiesService: TopicPropertiesService,
    private workspaceSessionService: WorkspaceSessionService,
  ) {
  }

  ngOnInit() {
    // build the form when topic properties are loaded or changed
    this.topicProperties$
      .pipe(
        // use filter to ignore changes that are triggered by form value changes
        filter((props) => !isEqual(props, this.props)),
        takeUntil(this.unsubscribe)
      ).subscribe(props => this.buildForm(props));

    // enable/disable checkbox controls, other controls are disabled in the template
    this.readonly$.pipe(takeUntil(this.unsubscribe))
      .subscribe((readonly) => {
        if (readonly) {
          this.form?.controls.searchable.disable({ emitEvent: false });
        } else {
          this.form?.controls.searchable.enable({ emitEvent: false });
        }
      });

    // get project variable sets
    this.variableSets$ = combineLatest([
      this.commitId$,
      this.projectId$
    ]).pipe(
      switchMap(([commitId, projectId]) => {
        const committedItem = {
          projectId: projectId,
          commitId: commitId,
          fileType: CollabFileType.Workspace,
          reviewFileId: null
        };
        return this.metaDataService.getProjectVariableSets$(committedItem)
          .pipe(
            takeUntil(this.unsubscribe),
            repeatWhileNull({ retryInterval: 2000 })
          );
      }),
      takeUntil(this.unsubscribe),
      shareReplay(1)
    );

    // listen to every file tree changes
    const fileTreeChange$ = merge(
      this.commitId$.pipe(skip(1)), // skip the initial value to process only changes
      this.workspaceSessionService.fileDeleted$,
      this.workspaceSessionService.fileRenamed$,
      this.workspaceSessionService.fileInserted$,
    );

    // reload file pickers and revalidate the form controls when the project file tree is changed
    combineLatest([
      fileTreeChange$,
      this.readonly$
    ]).pipe(
      filter(([_, readonly]) => !readonly), // ignore changes in readonly mode
      takeUntil(this.unsubscribe))
      .subscribe(() => this.reloadFilePickers());
  }

  /** Unsubscribes from observables to prevent memory leaks. */
  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Opens Insert Variable dialog and inserts selected variable into the title. */
  insertVariables(inputElement: HTMLInputElement): void {
    const data: InsertVariableDialogData = {
      projectVariableSets$: this.variableSets$,
      hiddenSystemVariables: this.variablesService.getSystemLinkedVariables(),
      readonly$: this.readonly$,
    };
    this.variablesInsertService.insertVariable(data, inputElement, this.form);
  }

  /** Builds the form for editing topic properties */
  private buildForm(props: TopicProperties): void {
    const form = this.formBuilder.group<TopicPropertiesForm>({
      title: new FormControl<string>(props?.title),
      stylesheets: new FormControl<string[]>(props?.stylesheets, {
        asyncValidators: this.formsService.createProjectStylesheetValidator(this.projectId, this.branchName, StylesheetType.General)
      }),
      templatePage: new FormControl<string[]>(makeArray(props?.templatePage), {
        asyncValidators: this.formsService.createProjectFileExistsValidator(this.projectId, this.branchName)
      }),
      searchable: new FormControl<boolean>({ value: props?.searchable, disabled: this.readonly }),
    });
    // mark file picker controls as touched to show validation errors (if any) when the form is built
    form.controls.stylesheets.markAsTouched();
    form.controls.templatePage.markAsTouched();

    // update the topic model when form value changes
    form.valueChanges.pipe(
      filter(() => !this.ignoreChanges),
      takeUntil(this.unsubscribe))
      .subscribe((value) => {
        const props = {
          title: value.title,
          stylesheets: value.stylesheets,
          templatePage: firstOrNull(value.templatePage),
          searchable: value.searchable,
        };
        // prevent rebuild form when topic properties changes are triggered by form value changes
        this.props = props;
        // notify other collab users about the topic changes
        this.topicPropertiesService.setTopicProperties(props);
      });

    // save the current topic properties
    this.props = props;
    // updates the form elements
    this.form = form;
  }

  /** Reload file pickers to update the loaded data in file picker popups
   *  and revalidate the form controls when file tree changes */
  private reloadFilePickers(): void {
    // clear the loaded data in file picker popups
    this.stylesheetInputs?.updatePopupData();
    this.templatePageInput?.updatePopupData();
    const controls = [
      this.form.controls.stylesheets,
      this.form.controls.templatePage,
    ];
    // prevent triggering form value changes when reloading file pickers
    this.ignoreChanges = true;
    // revalidate file picker controls
    controls.forEach(control => {
      // use onlySelf=true to revalidate the existing value without triggering valueChanges
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
    this.ignoreChanges = false;
  }

}
